import type { CampoPuntaje } from '@pasos/schemas';
import { useId } from 'react';
import { Angry, Frown, ICONO, Laugh, Meh, Smile } from '../../components/ui/iconos.js';
import { PACIENTE } from '../../copy/index.js';

type CarasProps = {
  campo: CampoPuntaje;
  valor: number | undefined;
  onElegir: (valor: number) => void;
  /** Id del encabezado de la pregunta: es el nombre accesible del grupo. */
  etiquetadoPor: string;
};

/**
 * Escala de caras del microcheck (P-05) · **el control con el que se responde**.
 *
 * ADR-016, enmienda del 25/08/2026: esta escala es el caso especial de la
 * iconografía. **No es decoración: es el control de respuesta**, y por eso no
 * puede depender de un emoji del sistema. El mismo carácter se dibuja distinto
 * en Android, en iOS y en Windows —y los emoji modernos son chicos y de bajo
 * contraste—, así que dos pacientes verían escalas distintas y contestarían
 * cosas distintas. Las caras salen de la librería: **todos ven exactamente la
 * misma escala**.
 *
 * Tres reglas que vienen de esa enmienda y que no son estéticas:
 *
 *   1. **Un solo peso, tamaño grande.** Íconos lineales de la librería, sin
 *      mezclar rellenos, al tamaño destacado del sistema. El público es un
 *      adulto mayor recién operado mirando un celular.
 *   2. **Cada opción lleva su etiqueta de texto visible.** Es el número de la
 *      escala, que además es lo que el paciente le dice al médico por teléfono.
 *      Una escala que depende del ícono para significar algo no cumple WCAG
 *      2.1 AA. Las caras van `aria-hidden`: lo que se anuncia es el número.
 *      Las **dos anclas del instrumento** vienen del campo y no de acá: en el
 *      reporte de dolor son las del NRS 0-10 (LOINC 72514-3). Los valores
 *      intermedios quedan sólo numéricos, porque el NRS no nombra sus puntos
 *      medios. Desde E3.11 van **arriba de la grilla y atadas a su número**
 *      ("0 · Sin dolor"): debajo quedaban bajo el 8 y el 10 y se leían como
 *      rótulos de esas celdas.
 *   3. **Se persiste el ordinal, nunca el carácter.** Lo que sale de acá es el
 *      número del rango del instrumento.
 *
 * **Las caras no llevan color de banda.** Pintar de verde a rojo convertiría el
 * reporte de dolor en un semáforo, y el dolor no puntúa ni enciende color
 * (definición de Fundación del 29/07, ratificada el 06/08). El color distingue
 * lo elegido de lo no elegido, nada más.
 *
 * No hay una cara por valor: son bandas. Con once caras distintas nadie
 * distingue la del 6 de la del 7, y el gesto de "elegir la cara que va" deja de
 * funcionar.
 *
 * E3.8.2, contra la referencia P-05: celdas de 76 px en cuatro columnas; la
 * elegida suma halo teal además del contorno, así que se lee sin depender del
 * color.
 *
 * El eco de la respuesta ("Tu respuesta: 3") lo pone el `Formulario` debajo del
 * control, con la cara del valor elegido: desde E3.9 (P-74) el paciente elige y
 * confirma con "Siguiente", en vez de avanzar al tocar.
 */
const CARAS = [Laugh, Smile, Meh, Frown, Angry] as const;

/** La cara de un valor de la escala. Exportada para el eco de la respuesta. */
export function caraDe(valor: number, minimo: number, maximo: number) {
  const proporcion = (valor - minimo) / Math.max(maximo - minimo, 1);
  const indice = Math.min(Math.floor(proporcion * CARAS.length), CARAS.length - 1);
  return CARAS[indice] ?? Meh;
}

export function Caras({ campo, valor, onElegir, etiquetadoPor }: CarasProps) {
  const grupo = useId();
  const anclas = useId();
  const conAnclas = Boolean(campo.etiquetaMinimo && campo.etiquetaMaximo);
  const valores = Array.from(
    { length: campo.maximo - campo.minimo + 1 },
    (_, indice) => campo.minimo + indice,
  );

  return (
    <fieldset
      aria-describedby={conAnclas ? anclas : undefined}
      aria-labelledby={etiquetadoPor}
      className="m-0 flex flex-col gap-4 border-0 p-0"
    >
      {/*
        Las anclas del instrumento, entre la pregunta y las opciones, cada una
        con su número. Son la descripción del grupo: un lector de pantalla las
        lee después de la pregunta y antes de las opciones.
      */}
      {conAnclas ? (
        <ul className="m-0 flex list-none flex-col gap-1 p-0 text-base text-subtle" id={anclas}>
          <li>{PACIENTE.formulario.ancla(campo.minimo, campo.etiquetaMinimo ?? '')}</li>
          <li>{PACIENTE.formulario.ancla(campo.maximo, campo.etiquetaMaximo ?? '')}</li>
        </ul>
      ) : null}

      <div className="grid grid-cols-4 gap-3">
        {valores.map((numero) => {
          const Cara = caraDe(numero, campo.minimo, campo.maximo);
          const elegida = valor === numero;

          return (
            <label
              className={`flex min-h-19 cursor-pointer flex-col items-center justify-center gap-0.5 rounded-lg text-xl leading-tight font-bold outline-2 -outline-offset-2 has-focus-visible:ring-3 has-focus-visible:ring-ring has-focus-visible:ring-offset-2 ${
                elegida
                  ? 'bg-accent-soft text-heading shadow-halo-teal outline-accent-strong'
                  : 'bg-card text-subtle outline-border hover:outline-brand-soft'
              }`}
              key={numero}
            >
              <input
                checked={elegida}
                className="sr-only"
                name={grupo}
                onChange={() => onElegir(numero)}
                type="radio"
                value={numero}
              />
              <Cara aria-hidden="true" className={ICONO.destacado} />
              {numero}
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
